import { Text, useToast, VStack } from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { applyActionCode, sendEmailVerification } from 'firebase/auth';
import { useSearchParams } from 'react-router-dom';
import type { FirebaseError } from 'firebase/app';
import {
  AuthHeading,
  AuthLoadingScreen,
  AuthPageLayout,
  AuthSubtext,
  EmailDeliveryHint,
  PrimaryAuthButton,
  useAuthTheme,
} from '@/components/AuthCards/auth-ui';
import { FirebaseContext } from '../session/FirebaseContext';
import { buildAuthContinueUrl } from '@/helpers/auth/authUrls';
import {
  EMAIL_DELIVERY_TOAST_HINT,
  errorToastOptions,
  getAuthErrorMessage,
  isAuthUserEmailVerified,
  successToastOptions,
  waitForEmailVerified,
} from '../helpers';

type VerifyPhase = 'idle' | 'applying' | 'redirecting' | 'failed';

const RESEND_COOLDOWN_SECONDS = 60;

export default function EmailVerificationPage() {
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const actionHandled = useRef(false);
  const { linkColor } = useAuthTheme();

  const emailJustSent = !!(location.state as { emailJustSent?: boolean } | null)?.emailJustSent;
  const [resendCooldown, setResendCooldown] = useState(emailJustSent ? RESEND_COOLDOWN_SECONDS : 0);
  const [verifyPhase, setVerifyPhase] = useState<VerifyPhase>('idle');

  const authUser = ctx.auth.currentUser ?? ctx.user;
  const oobCode = searchParams.get('oobCode');
  const redirectTo = searchParams.get('redirect_to') ?? '/profile';

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const id = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (!oobCode || actionHandled.current) {
      return;
    }
    actionHandled.current = true;

    (async () => {
      setVerifyPhase('applying');
      try {
        await applyActionCode(ctx.auth, oobCode);
        setVerifyPhase('redirecting');
        await waitForEmailVerified(ctx.auth);
        await ctx.refreshAuthUser();
        toast(successToastOptions('Email verified', 'Your email has been verified.'));
        navigate(redirectTo, { replace: true });
      } catch (err) {
        const firebaseErr = err as FirebaseError;
        if (isAuthUserEmailVerified(ctx.auth)) {
          setVerifyPhase('redirecting');
          await ctx.refreshAuthUser();
          navigate(redirectTo, { replace: true });
          return;
        }
        setVerifyPhase('failed');
        toast(errorToastOptions('Verification failed', getAuthErrorMessage(firebaseErr)));
      }
    })();
  }, [ctx, navigate, oobCode, redirectTo, toast]);

  useEffect(() => {
    if (oobCode || verifyPhase !== 'idle' || ctx.authLoading) {
      return;
    }
    if (isAuthUserEmailVerified(ctx.auth)) {
      setVerifyPhase('redirecting');
      navigate(redirectTo, { replace: true });
    }
  }, [ctx.auth, ctx.authLoading, navigate, oobCode, redirectTo, verifyPhase]);

  useEffect(() => {
    if (ctx.authLoading || ctx.auth.currentUser || oobCode) {
      return;
    }
    navigate('/login', { replace: true });
  }, [ctx.authLoading, ctx.auth.currentUser, navigate, oobCode]);

  async function sendEmail() {
    if (!authUser) {
      toast(errorToastOptions('Error!', 'Please sign-in before you try to verify your email.'));
      return;
    }

    try {
      await sendEmailVerification(authUser, {
        url: buildAuthContinueUrl('/verifyEmail'),
        handleCodeInApp: true,
      });
      toast(
        successToastOptions(
          'Email sent!',
          `10 Teams sent a verification link. Check your email to verify your address. ${EMAIL_DELIVERY_TOAST_HINT}`,
        ),
      );
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const firebaseErr = err as FirebaseError;
      toast(errorToastOptions('Email not sent!', getAuthErrorMessage(firebaseErr)));
      if (firebaseErr.code === 'auth/too-many-requests') {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    }
  }

  const signOut = async () => {
    await ctx.auth.signOut();
    navigate('/rules', { replace: true });
  };

  if (oobCode && (verifyPhase === 'applying' || verifyPhase === 'redirecting')) {
    return (
      <AuthLoadingScreen
        message={verifyPhase === 'applying' ? 'Verifying your email…' : 'Taking you to the game…'}
      />
    );
  }

  if (verifyPhase === 'redirecting') {
    return <AuthLoadingScreen message='Taking you to the game…' />;
  }

  if (oobCode && verifyPhase === 'failed' && !isAuthUserEmailVerified(ctx.auth)) {
    return (
      <AuthPageLayout showLogo>
        <AuthHeading>Verification failed</AuthHeading>
        <AuthSubtext mb={4}>This link is invalid or has expired.</AuthSubtext>
        <PrimaryAuthButton onClick={() => navigate('/login', { replace: true })}>
          Log in
        </PrimaryAuthButton>
      </AuthPageLayout>
    );
  }

  if (oobCode && verifyPhase === 'idle') {
    return <AuthLoadingScreen message='Verifying your email…' />;
  }

  if (authUser && !ctx.authLoading) {
    if (isAuthUserEmailVerified(ctx.auth)) {
      return <AuthLoadingScreen message='Taking you to the game…' />;
    }

    return (
      <AuthPageLayout onBack={signOut} backLabel='Sign out'>
        <VStack align='stretch' spacing={5}>
          <AuthHeading>Verify your email</AuthHeading>
          {emailJustSent ? (
            <AuthSubtext>
              We&apos;ve sent a verification link from 10 Teams to the address below. Open your
              inbox and tap the link to continue.
            </AuthSubtext>
          ) : (
            <AuthSubtext>
              To complete your sign-up, we need to confirm your email address. Tap below and
              we&apos;ll send a verification link from 10 Teams.
            </AuthSubtext>
          )}
          <Text fontSize='sm' fontWeight='semibold' textAlign='center' color={linkColor}>
            {authUser.email}
          </Text>
          <PrimaryAuthButton
            onClick={sendEmail}
            isDisabled={resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `Resend available in ${resendCooldown}s`
              : emailJustSent
                ? 'Resend verification email'
                : 'Send verification email'}
          </PrimaryAuthButton>
          <AuthSubtext textAlign='center'>
            Check your email for a link from 10 Teams to complete sign-in.
          </AuthSubtext>
          <EmailDeliveryHint />
        </VStack>
      </AuthPageLayout>
    );
  }

  if (!ctx.authLoading && !ctx.auth.currentUser && !oobCode) {
    return <AuthLoadingScreen message='Loading…' />;
  }

  return <AuthLoadingScreen message='Loading…' />;
}
